import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { Table } from '../Table';
import type { TableColumn } from '../Table';
import { Button } from '../Button';
import { MagnifyingGlassIcon, ArrowUpTrayIcon, PlusIcon, EllipsisVerticalIcon, PencilIcon, EyeIcon, TrashIcon, NoSymbolIcon, GlobeAltIcon, BuildingOffice2Icon, ChevronDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import type { DocumentListProps, DocumentListColumn, DocumentListCompany, LookupFieldConfig, RowAction } from './MasterDocument.types';
import { Loading } from './components/loading';
import { CrossingClassMenu } from './CrossingClassMenu';
import { ConfirmDialog } from './DocumentModalList';
import { SearcherControl } from './FieldRenderer';
import { buildDependentLookupProps, isFilterValueEmpty } from './lookupFilters';
import { PrintIcon } from './icons';
import { useMasterDocumentTranslation } from './i18n';
import { MoreOptionsMenu } from './MoreOptionsMenu';
import { ROW_ACTION_BTN, ajustarTamanoIcono } from './rowActionStyles';
import { accionDeEstadoAlCanon, clasesDeTonoBoton, ACTIVAR_FG, type AccionTono } from './stateActionCanon';

// Íconos por defecto para acciones de fila sin ícono explícito.
const DEFAULT_ROW_ACTION_ICONS: Record<string, typeof TrashIcon> = {
  delete: TrashIcon,
  cancel: NoSymbolIcon,
};

// ─── Acciones por fila (canon MasterCrud) ──────────────────

/** Acción de fila ya normalizada: da igual de qué prop venga. */
interface AccionDeFila {
  key: string;
  label: string;
  icon: React.ReactNode;
  danger?: boolean;
  /** Tono del canon. Manda sobre `danger`, que se conserva por retrocompatibilidad. */
  tone?: AccionTono;
  /**
   * Destino de la acción cuando ES una navegación (ver/editar). Presente ⇒ se
   * pinta como `<a href>`; ausente ⇒ `<button>`, como todas las demás.
   * Ver `DocumentListProps.getRowHref` para el por qué.
   */
  href?: string;
  onClick: () => void;
}

/**
 * ¿Este clic lo tiene que atender el NAVEGADOR en vez de la SPA?
 *
 * Sí cuando el usuario pidió explícitamente otra ventana o pestaña: Ctrl (⌘ en
 * Mac), Shift, Alt, o un botón que no es el primario. En esos casos la acción por
 * defecto del `<a href>` es justo lo que se quiere, así que NO se cancela y NO se
 * corre el `onClick` de la SPA — cancelarlo es el error clásico que deja Ctrl+clic
 * comportándose como un clic normal.
 *
 * El clic con la RUEDA no llega hasta aquí: los navegadores lo despachan como
 * `auxclick`, no como `click`, así que sin handler de por medio el ancla abre la
 * pestaña nueva por su cuenta. Para ese gesto no hay nada que programar — lo
 * único que hay que hacer es no estorbarlo.
 */
function elNavegadorSeEncarga(e: React.MouseEvent): boolean {
  return e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0;
}

// ─── Geometría de los overlays anclados a un trigger (ADR-027 §P) ──────────
// Compartidas por el menú «Más acciones» de la fila y por el `CompanyDropdown`
// del pie de este mismo archivo: los dos se anclan igual y deben comportarse igual.

/** Aire entre el borde del overlay y el del viewport. Mismos valores que el
 *  `CompanySelector` de MasterCrud, para que los dos combos se comporten igual. */
const MARGEN_VIEWPORT = 8;
/** Separación del overlay al trigger. */
const SEPARACION_TRIGGER = 4;
/** Altura mínima que se le deja al menú de fila aunque el hueco sea menor (≈2 opciones).
 *  Sólo entra en juego cuando NINGUNO de los dos lados da más: sin este suelo, un trigger
 *  pegado al borde dejaría el menú sin alto útil. */
const ALTO_MINIMO_MENU_FILA = 96;

/**
 * Celda de acciones de una fila del listado, con la MISMA regla de reparto que
 * MasterCrud (`MasterCrudTable.tsx`, `MAX_DIRECT = 3`):
 *
 * - hasta 3 acciones visibles ⇒ las 3 se pintan de frente, sin kebab;
 * - 4 o más ⇒ 2 directas + el botón «Más acciones», que completa 3; el resto va
 *   dentro del desplegable.
 *
 * Prioriza «Ver» y «Editar» como directas —el equivalente aquí del `edit`/`link`
 * de MasterCrud—; si no existen, toma las dos primeras.
 *
 * El desplegable se pinta en un PORTAL a `document.body` con `position: fixed`.
 * Antes era un `absolute` dentro de la celda sticky, y con pocas filas el menú
 * quedaba recortado por el `overflow` del contenedor de la tabla: se abría por
 * debajo del pie y no había forma de verlo ni de pulsarlo (ADR-027 §P.2 — el
 * defecto no son los píxeles de desborde, son las opciones inalcanzables).
 * Se reposiciona al abrir y se cierra en `scroll` (en captura, porque el scroll
 * de un contenedor intermedio no burbujea) y en `resize`.
 *
 * El alto va acotado al hueco disponible contra el trigger (§P.1) y el menú scrollea
 * por dentro: cuando ni arriba ni abajo cabe entero, las opciones siguen siendo
 * alcanzables en vez de quedar fuera de pantalla sin scroll que las alcance (§P.2).
 */
function RowActionsCell({
  acciones,
  busy = false,
}: {
  acciones: AccionDeFila[];
  busy?: boolean;
}) {
  const { t } = useMasterDocumentTranslation();
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; altoMaximo: number; haciaArriba: boolean }>(
    { top: -9999, left: -9999, altoMaximo: ALTO_MINIMO_MENU_FILA, haciaArriba: false },
  );

  const reposition = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const mw = menuRef.current?.offsetWidth || 210;
    const mh = menuRef.current?.offsetHeight || 0;
    const espacioDebajo = window.innerHeight - r.bottom - SEPARACION_TRIGGER - MARGEN_VIEWPORT;
    const espacioEncima = r.top - SEPARACION_TRIGGER - MARGEN_VIEWPORT;
    // Si abajo no cabe, se abre hacia arriba. Es justo el caso de «2 registros»:
    // el menú medía más que el hueco entre la fila y el pie de la tabla.
    // El criterio es EL MISMO de antes: `mh > espacioDebajo` es la forma despejada de
    // `top + mh > window.innerHeight - 8` con `top = r.bottom + 4`. Esto no cambia hacia
    // qué lado abre el menú; sólo le pone techo al lado que elija.
    const haciaArriba = mh > 0 && mh > espacioDebajo;
    // Techo acotado al hueco, medido contra el trigger igual que hace `CompanyDropdown`
    // en este mismo archivo (ADR-027 §P.1). Sin él el menú declaraba `max-height: none` y
    // `overflow-y: visible`, así que cuando no cabía entero en el lado elegido sus últimas
    // opciones quedaban FÍSICAMENTE inalcanzables: ni el menú ni el documento ofrecían
    // scroll (§P.2 — el defecto no son los píxeles de desborde, son las opciones que no se
    // pueden pulsar). Con el techo, ese caso degrada a un menú con scroll propio.
    const altoMaximo = Math.round(Math.max(haciaArriba ? espacioEncima : espacioDebajo, ALTO_MINIMO_MENU_FILA));
    const top = haciaArriba ? Math.round(r.top - SEPARACION_TRIGGER) : Math.round(r.bottom + SEPARACION_TRIGGER);
    const left = Math.round(Math.max(MARGEN_VIEWPORT, r.right - mw));
    setPos({ top, left, altoMaximo, haciaArriba });
  }, []);

  useLayoutEffect(() => {
    if (open) reposition();
  }, [open, reposition]);

  // El menú se cierra solo al disparar una acción, así que un indicador DENTRO
  // del desplegable no se vería nunca: va en el disparador de la fila.
  useEffect(() => {
    if (busy) setOpen(false);
  }, [busy]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScroll);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Fila en vuelo: el indicador sustituye a las acciones, dentro de un botón
  // DESHABILITADO y no de un `div` suelto. Dos razones: sin el botón el usuario
  // vuelve a disparar «anular» sobre el mismo documento (la mitad no visual del
  // problema), y el botón conserva la caja de 28px, que es la que sostiene los
  // 45px de alto de fila del canon — un spinner desnudo encogería esa fila y
  // sólo esa.
  if (busy) {
    return (
      <div className="flex items-center justify-center">
        <button type="button" disabled className={ROW_ACTION_BTN}>
          <Loading size="sm" layout="inline" ariaLabel={t('master_document.chrome.actionRunning')} />
        </button>
      </div>
    );
  }

  if (acciones.length === 0) return null;

  // Reparto del canon: se decide ANTES de pintar para no «gastar» un hueco.
  const MAX_DIRECT = 3;
  let directas: AccionDeFila[];
  let enMenu: AccionDeFila[];
  if (acciones.length <= MAX_DIRECT) {
    directas = acciones;
    enMenu = [];
  } else {
    const prioritarias = acciones.filter((a) => ['view', 'edit'].includes(a.key)).slice(0, 2);
    const relleno = prioritarias.length < 2
      ? acciones.filter((a) => !prioritarias.includes(a)).slice(0, 2 - prioritarias.length)
      : [];
    directas = [...prioritarias, ...relleno];
    enMenu = acciones.filter((a) => !directas.includes(a));
  }

  return (
    <div className="flex items-center justify-center gap-3" onClick={(e) => e.stopPropagation()}>
      {directas.map((a) => {
        const contenido = (
          <span className="w-[15px] h-[15px] inline-flex items-center justify-center">
            {ajustarTamanoIcono(a.icon)}
          </span>
        );
        // MISMAS clases y MISMA caja (28x28 de `ROW_ACTION_BTN`) en las dos ramas:
        // el ancla no debe verse distinta del botón, sólo comportarse como enlace.
        // `no-underline` porque el reset de la app puede subrayar los `<a>`, y el
        // botón hermano no va subrayado.
        const clases = clsx(ROW_ACTION_BTN, clasesDeTonoBoton(a.tone), a.href && 'no-underline');
        const estilo = a.tone === 'success' ? { color: ACTIVAR_FG } : undefined;

        // Acción de NAVEGACIÓN con destino conocido ⇒ ancla real. Le da al usuario
        // Ctrl/⌘+clic, clic con la rueda y «Abrir en pestaña nueva» del menú
        // contextual sin que el kit programe ninguno de los tres.
        if (a.href) {
          return (
            <a
              key={a.key}
              href={a.href}
              title={a.label}
              aria-label={a.label}
              onClick={(e) => {
                // Gesto de «otra pestaña»: se deja pasar tal cual. Ni
                // `preventDefault` ni `onClick` de la SPA — sólo se corta la
                // propagación para que el `onRowClick` de la fila no se dispare
                // además de la navegación.
                if (elNavegadorSeEncarga(e)) { e.stopPropagation(); return; }
                e.preventDefault();
                e.stopPropagation();
                a.onClick();
              }}
              className={clases}
              style={estilo}
            >
              {contenido}
            </a>
          );
        }

        return (
          <button
            key={a.key}
            type="button"
            title={a.label}
            aria-label={a.label}
            onClick={(e) => { e.stopPropagation(); a.onClick(); }}
            className={clases}
            style={estilo}
          >
            {contenido}
          </button>
        );
      })}

      {enMenu.length > 0 && (
        <>
          <button
            ref={btnRef}
            type="button"
            title={t('master_document.list.moreActions')}
            aria-label={t('master_document.list.moreActions')}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
            className={ROW_ACTION_BTN}
          >
            <EllipsisVerticalIcon className="w-[15px] h-[15px]" />
          </button>
          {open && createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{
                position: 'fixed',
                left: pos.left,
                // Al abrir hacia arriba se ancla por ABAJO, para que crezca alejándose del
                // trigger. Anclado por `top` un menú más alto que el hueco de encima se
                // colocaba en negativo y quedaba recortado por la cabecera: el mismo defecto
                // de §P.2, sólo que por el otro borde.
                ...(pos.haciaArriba
                  ? { bottom: Math.max(0, window.innerHeight - pos.top) }
                  : { top: pos.top }),
                maxHeight: pos.altoMaximo,
                overflowY: 'auto',
                // Evita que, al llegar al final de la lista, la rueda arrastre la página
                // que hay detrás (§P.3).
                overscrollBehavior: 'contain',
              }}
              className="z-[60] min-w-[210px] bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-md shadow-md py-1"
            >
              {enMenu.map((a) => (
                <button
                  key={a.key}
                  type="button"
                  role="menuitem"
                  onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors disabled:opacity-40',
                    a.tone === 'danger' || a.danger
                      ? 'text-error-content hover:bg-error-bg/10'
                      : 'text-content-primary hover:bg-background-secondary dark:text-dark-content-primary dark:hover:bg-dark-bg-secondary',
                  )}
                  style={a.tone === 'success' ? { color: ACTIVAR_FG } : undefined}
                >
                  <span className="w-4 h-4 flex-shrink-0 inline-flex items-center justify-center">{a.icon}</span>
                  <span>{a.label}</span>
                </button>
              ))}
            </div>,
            document.body,
          )}
        </>
      )}
    </div>
  );
}


/**
 * Control del panel de búsqueda avanzada, literal del canon
 * (`MasterCrudAdvancedSearch`): 14px, `rounded-md`, y el foco con borde
 * `#93d1fd` más el anillo tenue de la marca en vez del `primary-600` plano.
 */
const ADV_CONTROL_CLS =
  'w-full px-3 py-2 text-sm border border-border-primary dark:border-dark-border-primary rounded-md ' +
  'bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary ' +
  'focus:outline-none focus:border-[#93d1fd] focus:ring-[3px] focus:ring-primary-500/10 transition-colors';

// ─── Company Dropdown ──────────────────────────────────────

/** Altura mínima que se considera usable para desplegar hacia abajo (≈5 opciones). */
const ALTO_MINIMO_UTIL = 180;

function CompanyDropdown({
  companies,
  selectedCompany,
  onCompanyChange,
}: {
  companies: DocumentListCompany[];
  selectedCompany?: DocumentListCompany;
  onCompanyChange?: (company: DocumentListCompany) => void;
}) {
  const { t } = useMasterDocumentTranslation();
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; altoMaximo: number; haciaArriba: boolean }>({ top: 0, left: 0, altoMaximo: ALTO_MINIMO_UTIL, haciaArriba: false });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Alto máximo acotado al viewport, medido contra el trigger (ADR-027 §P.1).
  // Sin esto la lista crece tanto como compañías haya y, como el menú declaraba
  // `max-height: none`, las últimas quedaban FÍSICAMENTE inalcanzables: ni el menú ni el
  // documento ofrecían scroll (§P.2). Se mide en cada apertura, y en `resize` y `scroll`,
  // porque la altura disponible depende de dónde esté el trigger — en el listado va bajo
  // la cabecera y en el detalle mucho más abajo, así que un `vh` fijo acertaría en un caso
  // y fallaría en el otro.
  const updatePos = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const espacioDebajo = window.innerHeight - rect.bottom - SEPARACION_TRIGGER - MARGEN_VIEWPORT;
    const espacioEncima = rect.top - SEPARACION_TRIGGER - MARGEN_VIEWPORT;
    // Sólo se voltea si abajo no cabe lo mínimo Y arriba hay más sitio: abrir hacia arriba
    // cuando abajo alcanza sería mover el menú sin motivo.
    const haciaArriba = espacioDebajo < ALTO_MINIMO_UTIL && espacioEncima > espacioDebajo;
    // Se redondea porque es un píxel de CSS: un rect fraccionario (zoom, transform de un
    // ancestro) daría un valor distinto en cada evento, y esto cuelga de `scroll` en captura,
    // que dispara por frame. Y sólo se re-renderiza si el resultado CAMBIA (§P.4).
    const altoMaximo = Math.round(Math.max(haciaArriba ? espacioEncima : espacioDebajo, ALTO_MINIMO_UTIL));
    const top = haciaArriba ? Math.round(rect.top - SEPARACION_TRIGGER) : Math.round(rect.bottom + SEPARACION_TRIGGER);
    const left = Math.round(rect.left);
    setMenuPos((prev) =>
      prev.top === top && prev.left === left && prev.altoMaximo === altoMaximo && prev.haciaArriba === haciaArriba
        ? prev
        : { top, left, altoMaximo, haciaArriba });
  }, []);

  const handleToggle = () => {
    if (!open) updatePos();
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (
        triggerRef.current?.contains(e.target as Node) ||
        menuRef.current?.contains(e.target as Node)
      ) return;
      setOpen(false);
    }
    function onScroll() { updatePos(); }
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updatePos);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, updatePos]);

  const isGlobal = !selectedCompany || String(selectedCompany.uuid) === '000';
  const Icon = isGlobal ? GlobeAltIcon : BuildingOffice2Icon;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        aria-haspopup="menu"
        aria-expanded={open}
        // Trigger del canon (`CompanySelector` de MasterCrud, medido en /app/items el
        // 2026-09-04): 28px de alto, radio 6px, padding 6/12, fondo #eff8ff, borde #93d1fd
        // y texto #1355b7. Antes era una píldora de 26px con el azul de Google
        // (#E8F0FE/#1A73E8/#1557B0), que no es el azul de marca del producto.
        className={clsx(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold leading-none select-none transition-colors',
          'bg-[#eff8ff] border border-[#93d1fd] text-[#1355b7] cursor-pointer',
          'hover:bg-[#dbeefe] hover:border-[#3c9bf6]',
          'dark:bg-primary-900/20 dark:text-primary-300 dark:border-primary-800 dark:hover:bg-primary-900/40',
        )}
      >
        <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
        <span className="max-w-[180px] truncate" title={selectedCompany?.name}>
          {selectedCompany?.name ?? t('master_document.list.selectCompany')}
        </span>
        <ChevronDownIcon className={clsx('w-3 h-3 shrink-0 transition-transform', open && 'rotate-180')} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            left: menuPos.left,
            // Al abrir hacia arriba se ancla por abajo, para que crezca alejándose del trigger.
            ...(menuPos.haciaArriba
              ? { bottom: Math.max(0, window.innerHeight - menuPos.top) }
              : { top: menuPos.top }),
            maxHeight: menuPos.altoMaximo,
            overflowY: 'auto',
            // Evita que, al llegar al final de la lista, la rueda arrastre la página detrás (§P.3).
            overscrollBehavior: 'contain',
          }}
          className={clsx(
            'z-[9999] min-w-[280px] max-w-[380px]',
            'rounded-lg border border-border-primary shadow-md py-1',
            'bg-white dark:bg-dark-bg-primary dark:border-dark-border-primary',
          )}
        >
          {companies.map((company, idx) => {
            const optIsGlobal = String(company.uuid) === '000';
            const OptIcon = optIsGlobal ? GlobeAltIcon : BuildingOffice2Icon;
            const active = selectedCompany?.uuid === company.uuid || (isGlobal && optIsGlobal);
            // Divisor entre la opción Global y las compañías reales, como en el canon.
            const abreGrupoReal = !optIsGlobal && idx > 0 && String(companies[idx - 1].uuid) === '000';
            return (
              <div key={String(company.uuid)}>
                {abreGrupoReal && (
                  <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
                )}
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { onCompanyChange?.(company); setOpen(false); }}
                  // Opción del canon: UNA línea — check azul de la activa, icono de contexto,
                  // nombre y código a la derecha. Antes eran dos líneas apiladas y la activa se
                  // marcaba con `bg-purple-50`, un morado que no está en la paleta del producto.
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors cursor-pointer',
                    'text-content-primary dark:text-dark-content-primary',
                    'hover:bg-background-secondary dark:hover:bg-dark-bg-secondary',
                  )}
                >
                  <span className="w-3 shrink-0 text-[#0e79fd]">
                    {active && <CheckIcon className="w-3 h-3" />}
                  </span>
                  <span className="shrink-0 text-[#1355b7] dark:text-primary-300">
                    <OptIcon className="w-3.5 h-3.5" />
                  </span>
                  <span className="font-medium truncate min-w-0" title={company.name}>{company.name}</span>
                  {!optIsGlobal && company.code != null && (
                    <span className="ml-auto shrink-0 text-content-secondary dark:text-dark-content-secondary tabular-nums">
                      {company.code}
                    </span>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Control del embudo de una columna foránea ─────────────

/**
 * Control del embudo de una columna FORÁNEA: el mismo searcher (`LookupField`)
 * que usa el formulario del documento, en vez de un campo donde haya que
 * conocer y teclear el id.
 *
 * Emite el valor del `valueField` del registro elegido (por defecto `Id`) —lo
 * que la columna filtra— y `undefined` al limpiar, para que el contenedor
 * descarte la clave en lugar de mandar un filtro vacío.
 *
 * Sin `searcher` o sin `fetcher`, `SearcherControl` degrada solo al input de
 * texto con lupa: nunca deja la columna sin filtro.
 */
function LookupColumnFilter({
  field,
  searcher,
  placeholder,
  values,
  onChange,
  fetcher,
}: {
  field: string;
  searcher?: LookupFieldConfig;
  placeholder?: string;
  /** Filtros activos: de aquí sale el valor propio y los padres de la cascada. */
  values: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  fetcher?: unknown;
}) {
  // Cascada ENTRE embudos de la grilla (p.ej. Sucursal filtrada por el Cliente
  // ya elegido): el padre se resuelve contra los filtros activos.
  const dep = buildDependentLookupProps(searcher, (parentField) => values[parentField]);

  return (
    // `--lf-field-gap` a 0 porque el rótulo lo pinta el encabezado y la label del
    // LookupField queda vacía: ese hueco sólo desalinearía el control. La altura
    // reducida es la métrica del embudo, que vive en una celda de encabezado.
    <div style={{ '--lf-field-gap': '0rem', '--lf-trigger-height': '1.625rem' } as CSSProperties}>
      <SearcherControl
        value={values[field] ?? null}
        onChange={(v) => onChange(field, v ?? undefined)}
        placeholder={placeholder}
        hasError={false}
        lookupConfig={searcher}
        fetcher={fetcher}
        getLookupFilters={dep?.getFilters}
        lookupDependencies={dep?.dependencies}
        disabled={dep?.parentMissing}
      />
    </div>
  );
}

// ─── Embudo de filtro por columna ──────────────────────────

/** Campo por el que filtra una columna: el declarado, o el de sus datos. */
function columnFilterField(col: DocumentListColumn): string {
  return col.filter?.field ?? col.dataKey ?? col.key;
}

/** Icono de embudo del encabezado (mismo trazo que el resto de la grilla). */
function FunnelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

/**
 * Encabezado de columna con embudo: el rótulo y, si la columna declara `filter`,
 * un botón que despliega el control debajo — texto, fecha, select o searcher.
 *
 * Una columna FORÁNEA (`type: 'lookup'`) despliega el mismo `LookupField` del
 * formulario y filtra por el id del registro elegido: es el caso que hoy no se
 * podía filtrar sin conocer y teclear el código.
 *
 * El embudo queda MARCADO mientras el filtro tenga valor, para que un filtro
 * activo con el panel plegado no sea invisible. El control se despliega solo si
 * ya hay valor (un filtro activo no se esconde al recargar la página).
 */
function ColumnHeaderFilter({
  col,
  value,
  onChange,
  fetcher,
  columnFilters,
}: {
  col: DocumentListColumn;
  value: unknown;
  onChange: (field: string, value: unknown) => void;
  fetcher?: unknown;
  columnFilters: Record<string, unknown>;
}) {
  const { t } = useMasterDocumentTranslation();
  const filter = col.filter!;
  const field = columnFilterField(col);
  const active = !isFilterValueEmpty(value);
  const [open, setOpen] = useState(active);
  const [text, setText] = useState(value == null ? '' : String(value));

  // Debounce del texto: filtrar server-side por cada tecla es una petición por
  // pulsación. Los demás controles (select/fecha/searcher) emiten al instante.
  useEffect(() => {
    if (filter.type !== 'text') return;
    const current = value == null ? '' : String(value);
    if (text === current) return;
    const id = window.setTimeout(() => onChange(field, text === '' ? undefined : text), 350);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  // Un filtro que el contenedor limpió por fuera (botón "Limpiar") debe vaciar
  // también la caja de texto; sin esto seguiría mostrando lo tecleado.
  useEffect(() => {
    if (filter.type !== 'text') return;
    if (isFilterValueEmpty(value)) setText('');
  }, [value, filter.type]);

  // Control del embudo, literal del canon (MasterCrudTable.tsx:134): 11px sobre blanco y
  // foco con el borde #93d1fd más el anillo tenue de la marca, no el `primary-600` plano.
  // `font-normal normal-case tracking-normal` se conservan porque el input hereda de la
  // celda de cabecera y hay que neutralizar su peso.
  const controlCls = 'w-full min-w-0 h-[26px] box-border border border-border-primary dark:border-dark-border-primary rounded-[5px] px-1.5 text-[11px] font-normal normal-case tracking-normal text-content-primary dark:text-dark-content-primary bg-white dark:bg-dark-bg-primary outline-none focus:border-[#93d1fd] focus:ring-2 focus:ring-primary-500/10 transition-colors';

  return (
    // Cabecera del embudo, literal del canon (`ColumnFilterHeader` de
    // MasterCrudTable): el rótulo a la izquierda con la tipografía de encabezado
    // —14px/600 en `content-primary`— y el embudo empujado al borde derecho por
    // `justify-between`. La banda de 28px es la que iguala el alto con el de una
    // columna SIN filtro, para que la fila de cabecera no cambie de altura según
    // qué columnas filtren.
    <div className="flex flex-col gap-1 w-full">
      <div className="flex items-center justify-between w-full gap-1 min-h-[28px] select-none">
        <span className="text-sm font-semibold truncate min-w-0 text-content-primary dark:text-dark-content-primary">{col.header}</span>
        <button
          type="button"
          aria-expanded={open}
          aria-label={`${t('master_document.list.filterColumn')}: ${col.header}`}
          title={`${t('master_document.list.filterColumn')}: ${col.header}`}
          onClick={() => setOpen((o) => !o)}
          className={clsx(
            'shrink-0 inline-flex items-center justify-center p-0.5 rounded transition-colors',
            'hover:bg-[#eff8ff] dark:hover:bg-primary-900/20',
            open || active
              ? 'text-primary-600 hover:text-primary-600'
              : 'text-content-secondary hover:text-primary-600',
          )}
        >
          <FunnelIcon className="w-3 h-3" />
        </button>
      </div>

      {open && (
        <div className="font-normal normal-case tracking-normal">
          {filter.type === 'lookup' ? (
            <LookupColumnFilter
              field={field}
              searcher={filter.searcher}
              placeholder={filter.placeholder}
              values={columnFilters}
              onChange={onChange}
              fetcher={fetcher}
            />
          ) : filter.type === 'select' ? (
            <select
              className={clsx(controlCls, 'cursor-pointer')}
              value={value == null ? '' : String(value)}
              onChange={(e) => onChange(field, e.target.value === '' ? undefined : e.target.value)}
              aria-label={`${t('master_document.list.filterColumn')}: ${col.header}`}
            >
              <option value="">{t('master_document.list.filterAll')}</option>
              {filter.options?.map((opt) => (
                <option key={String(opt.value)} value={String(opt.value)}>{opt.label}</option>
              ))}
            </select>
          ) : filter.type === 'date' ? (
            <input
              type="date"
              className={controlCls}
              // ADR-027 §O.9: un control nativo de fecha no puede encoger — el navegador
              // dibuja su contenido y lo RECORTA en vez de poner elipsis. En una columna de
              // 90px el valor se lee `31/10/202` con el glifo del calendario encima del año,
              // y la sonda no lo ve porque Chrome comprime su layout interno y deja
              // `scrollWidth === clientWidth`. `min-content` deja ganar al ancho intrínseco;
              // nunca el 114px medido, que se mueve con la fuente y el navegador. Si la
              // columna es más estrecha, el scroll horizontal de la tabla es el desenlace
              // correcto; un valor ilegible no lo es.
              style={{ minWidth: 'min-content' }}
              value={value == null ? '' : String(value)}
              onChange={(e) => onChange(field, e.target.value === '' ? undefined : e.target.value)}
              aria-label={`${t('master_document.list.filterColumn')}: ${col.header}`}
            />
          ) : (
            <input
              type="text"
              size={1}
              autoComplete="off"
              className={controlCls}
              placeholder={filter.placeholder ?? t('master_document.list.filterPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
              aria-label={`${t('master_document.list.filterColumn')}: ${col.header}`}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ─── DocumentList ──────────────────────────────────────────

/**
 * Vista de lista de documentos con filtros, búsqueda avanzada y paginación.
 *
 * Renderiza un encabezado con título, chip de compañía y acciones (exportar,
 * crear nuevo), una barra de pills de estado, un panel de búsqueda avanzada
 * colapsable, la tabla de resultados y controles de paginación.
 *
 * @see docs/colors.md - Sistema de tokens de color
 * @see MasterDocument.types - DocumentListProps
 *
 * @example
 * ```tsx
 * <DocumentList
 *   title="Pedidos de Venta"
 *   companyName="Siesa SAS"
 *   pills={[{ key: 'all', label: 'Todos', count: 42, active: true }]}
 *   columns={[{ key: 'number', header: 'Número' }]}
 *   rows={data}
 *   totalCount={42}
 *   page={1}
 *   pageSize={10}
 *   onPageChange={setPage}
 *   onRowClick={(row) => navigate(row.id)}
 *   {...otherProps}
 * />
 * ```
 */
export function DocumentList({
  title,
  subtitle,
  companies,
  selectedCompany,
  companyName,
  onCompanyChange,
  onExport,
  onCreateNew,
  createNewLabel,
  canCreate = true,
  pills,
  onPillClick,
  searchFields,
  searchValues,
  onSearchChange,
  fetcher,
  columnFilters = {},
  onColumnFilterChange,
  onSearch,
  onClearSearch,
  searchOpen,
  onToggleSearch,
  columns,
  rows,
  totalCount,
  onRowClick,
  onViewRow,
  onEditRow,
  getRowHref,
  onDeleteRow,
  getRowActions,
  busyRowIds,
  onPrintRow,
  isPrintRowAvailable,
  printLabel,
  bulkMode = false,
  selectedRowIds = [],
  onRowSelect,
  getRowId = (row) => String(row.id ?? row.uuid ?? ''),
  bulkConvertOptions,
  onBulkConvert,
  emptyState,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  quickSearchEnabled = false,
  quickSearchPlaceholder,
  quickSearchValue,
  onQuickSearchChange,
  loading,
  error,
  onRetry,
  className,
}: DocumentListProps) {
  const { t } = useMasterDocumentTranslation();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Confirmación de acciones de fila (DEF-4 / DEF-3): respeta
  // `requiresConfirmation` usando el ConfirmDialog del kit en lugar de window.confirm.
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; run: () => void } | null>(null);

  const handleRowAction = useCallback((action: RowAction, row: Record<string, unknown>) => {
    if (action.requiresConfirmation) {
      setPendingConfirm({
        message: action.confirmationMessage ?? t('master_document.list.confirmAction'),
        run: () => { setPendingConfirm(null); action.onClick(row); },
      });
    } else {
      action.onClick(row);
    }
  }, [t]);

  // Una fila ofrece acciones si hay ver/editar, borrado legacy, acciones por
  // estado o impresión nativa.
  const hasRowMenu = !!(onViewRow || onEditRow || onDeleteRow || getRowActions || onPrintRow);

  /**
   * Normaliza a UNA lista las acciones que llegan por cinco props distintas, para
   * que el reparto «3 directas / 2 + kebab» del canon se decida sobre el conjunto
   * real y no por el origen de cada acción.
   *
   * Se conserva la precedencia previa: `getRowActions` gobierna lo destructivo y
   * deja fuera el `onDeleteRow` legacy; «Imprimir» solo aparece si la fila lo
   * admite. Es el MISMO criterio que antes decidía qué entraba en el desplegable.
   */
  const buildRowActions = useCallback((row: Record<string, unknown>): AccionDeFila[] => {
    const lista: AccionDeFila[] = [];
    // «Ver» ya NO sale por defecto: con la resolución interna de MasterDocumentPage
    // llevaba al MISMO detalle que «Editar» —la editabilidad la decide el estado del
    // documento, no la puerta por la que se entra— y MasterCrud no tiene ese botón.
    // Una segunda puerta al mismo sitio gastaba uno de los tres huecos directos.
    //
    // Sobrevive como OPT-IN porque hay listados donde no es la misma puerta:
    // despachos y notas crédito tienen ruta de sólo lectura (`/$id`) distinta de la
    // de edición (`/$id/edit`), y quitarla a secas dejaba esa ruta inalcanzable desde
    // el listado. `MasterDocumentPage` sólo pasa este callback cuando el host declaró
    // el suyo, así que el default no lo trae.
    // `href` sólo lo llevan «Ver» y «Editar»: son las DOS acciones que navegan a
    // una URL. Anular, borrar o imprimir mutan o abren un modal —no tienen destino
    // que abrir en otra pestaña— y por eso siguen siendo botones.
    if (onViewRow) {
      lista.push({ key: 'view', label: t('master_document.list.viewDetail'), icon: <EyeIcon className="w-4 h-4" />, href: getRowHref?.(row, 'view'), onClick: () => onViewRow(row) });
    }
    if (onEditRow) {
      lista.push({ key: 'edit', label: t('master_document.lines.edit'), icon: <PencilIcon className="w-4 h-4" />, href: getRowHref?.(row, 'edit'), onClick: () => onEditRow(row) });
    }
    if (onPrintRow && (isPrintRowAvailable?.(row) ?? true)) {
      lista.push({ key: 'print', label: printLabel ?? t('master_document.list.print'), icon: <PrintIcon className="w-4 h-4" />, onClick: () => onPrintRow(row) });
    }
    const porEstado = getRowActions?.(row);
    if (porEstado) {
      porEstado.forEach((a) => {
        const canon = accionDeEstadoAlCanon(a, t);
        const DefaultIcon = DEFAULT_ROW_ACTION_ICONS[a.key];
        lista.push({
          key: a.key,
          label: canon?.label ?? a.label,
          danger: canon ? canon.tone === 'danger' : a.variant === 'danger',
          tone: canon?.tone,
          icon: canon ? <canon.Icon className="w-4 h-4" /> : (a.icon ?? (DefaultIcon ? <DefaultIcon className="w-4 h-4" /> : <EllipsisVerticalIcon className="w-4 h-4" />)),
          onClick: () => handleRowAction(a, row),
        });
      });
    } else if (onDeleteRow) {
      // Legacy: borrar gobernado por flag global (retrocompat).
      lista.push({ key: 'delete', label: t('master_document.list.action.delete'), danger: true, tone: 'danger', icon: <TrashIcon className="w-4 h-4" />, onClick: () => onDeleteRow(row) });
    }
    return lista;
  }, [onViewRow, onEditRow, getRowHref, onPrintRow, isPrintRowAvailable, printLabel, getRowActions, onDeleteRow, handleRowAction, t]);

  const todasSeleccionadas = rows.length > 0 && selectedRowIds.length === rows.length;
  const algunaSeleccionada = selectedRowIds.length > 0 && selectedRowIds.length < rows.length;

  const alternarTodas = useCallback((marcar: boolean) => {
    rows.forEach((row) => {
      const id = getRowId(row);
      const yaEsta = selectedRowIds.includes(id);
      if (marcar && !yaEsta) onRowSelect?.(id, true);
      if (!marcar && yaEsta) onRowSelect?.(id, false);
    });
  }, [rows, selectedRowIds, onRowSelect, getRowId]);

  /**
   * Columnas para el `Table` del kit — el MISMO componente que usa MasterCrud a
   * través de `MasterCrudTable`.
   *
   * Antes esta tabla se pintaba a mano (banda de cabecera, filas, celda sticky y
   * un paginador propio), y por eso divergía: esquinas, densidad, embudos y
   * paginación eran una imitación que había que mantener sincronizada. Al montar
   * el `Table` con las mismas props que pasa MasterCrud, la apariencia deja de
   * ser una copia y pasa a ser el mismo componente (ADR-027 §E.17).
   */
  const tableColumns = useMemo<TableColumn<Record<string, unknown>>[]>(() => {
    const cols: TableColumn<Record<string, unknown>>[] = [];

    if (bulkMode) {
      cols.push({
        header: (
          <input
            type="checkbox"
            checked={todasSeleccionadas}
            ref={(el) => { if (el) el.indeterminate = algunaSeleccionada; }}
            onChange={(e) => alternarTodas(e.target.checked)}
            className="w-4 h-4 rounded accent-primary-custom-600 cursor-pointer"
            aria-label={t('master_document.list.selectAll')}
          />
        ),
        headerRaw: true,
        accessor: (() => null) as never,
        align: 'center',
        width: '48px',
        fixedWidth: true,
        render: (_v, row) => (
          <input
            type="checkbox"
            checked={selectedRowIds.includes(getRowId(row))}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => onRowSelect?.(getRowId(row), e.target.checked)}
            className="w-4 h-4 rounded accent-primary-custom-600 cursor-pointer"
            aria-label={t('master_document.list.selectRow')}
          />
        ),
      });
    }

    columns.forEach((col) => {
      const conEmbudo = !!(col.filter && onColumnFilterChange);
      cols.push({
        header: conEmbudo
          ? (
            <ColumnHeaderFilter
              col={col}
              value={columnFilters[columnFilterField(col)]}
              onChange={onColumnFilterChange!}
              fetcher={fetcher}
              columnFilters={columnFilters}
            />
          )
          : col.header,
        headerRaw: conEmbudo,
        accessor: (() => null) as never,
        align: col.align,
        width: col.width,
        // El `render` emite CONTENIDO, sin padding propio: la celda del kit ya
        // trae el suyo (8/16 con `dense`) y pagarlo dos veces engorda la fila
        // sin nada en pantalla que lo explique (ADR-027 §E.8). El `span` es solo
        // control de desborde, que esa misma regla permite.
        render: (_v, row) => (
          <span className={clsx('block w-full min-w-0 truncate', col.mono && 'tabular-nums font-medium')}>
            {col.render ? col.render(row) : (String(row[col.dataKey ?? col.key] ?? '').trim() || '—')}
          </span>
        ),
      });
    });

    if (hasRowMenu) {
      cols.push({
        header: t('master_document.list.rowActions'),
        accessor: (() => null) as never,
        align: 'center',
        // Literal del canon (MasterCrudTable): 3×28px + 2×12px de gap = 108px
        // + 32px de padding de celda = 140px, y `fixedWidth` para que la columna
        // no se expanda a repartir el sobrante (§E.5).
        width: '140px',
        fixedWidth: true,
        stickyRight: true,
        render: (_v, row) => (
          <RowActionsCell
            acciones={buildRowActions(row)}
            busy={busyRowIds?.includes(getRowId(row))}
          />
        ),
      });
    }

    return cols;
  }, [columns, bulkMode, todasSeleccionadas, algunaSeleccionada, alternarTodas, selectedRowIds, onRowSelect, getRowId, columnFilters, onColumnFilterChange, fetcher, hasRowMenu, buildRowActions, busyRowIds, t]);

  // Hay filtro puesto si el usuario tocó la búsqueda básica, el panel avanzado o
  // un embudo de columna. Sin esto, una búsqueda sin resultados pintaría el
  // estado vacío de «aún no tienes documentos», que es un mensaje falso.
  const hayFiltroActivo = !!(
    (quickSearchValue ?? '').trim() ||
    Object.values(searchValues).some((v) => !isFilterValueEmpty(v)) ||
    Object.values(columnFilters).some((v) => !isFilterValueEmpty(v))
  );

  const paginacion = {
    currentPage: page,
    totalPages,
    onPageChange,
    pageSize,
    pageSizeOptions: [10, 25, 50, 100],
    onPageSizeChange,
    totalRecords: totalCount,
    previousLabel: t('master_document.list.previousPage'),
    nextLabel: t('master_document.list.nextPage'),
    perPageLabel: t('master_document.list.perPage'),
    recordsLabel: t('master_document.list.records'),
    goToPageLabel: t('master_document.list.goToPage'),
  };

  // Caja de la búsqueda básica, con el toggle del panel avanzado DENTRO — es
  // donde lo pone MasterCrud, no en una fila propia con su cabecera.
  const cajaBusqueda = quickSearchEnabled && onQuickSearchChange ? (
    <div className="relative flex items-center">
      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-tertiary dark:text-dark-content-tertiary pointer-events-none">
        <MagnifyingGlassIcon className="w-3.5 h-3.5" />
      </span>
      <input
        type="search"
        placeholder={quickSearchPlaceholder ?? t('master_document.field.search')}
        value={quickSearchValue ?? ''}
        autoComplete="off"
        onChange={(e) => onQuickSearchChange(e.target.value)}
        className={clsx(
          'pl-8 h-9 w-64 text-sm border border-border-primary dark:border-dark-border-primary rounded-md',
          'bg-white dark:bg-dark-bg-primary text-content-primary dark:text-dark-content-primary',
          'placeholder:text-content-tertiary focus:outline-none focus:border-[#93d1fd]',
          'focus:ring-[3px] focus:ring-primary-500/10 transition-colors disabled:opacity-60',
          onToggleSearch ? 'pr-10' : 'pr-3',
        )}
      />
      {onToggleSearch && (
        <button
          type="button"
          onClick={onToggleSearch}
          aria-expanded={!!searchOpen}
          title={searchOpen ? t('master_document.list.hideAdvancedSearch') : t('master_document.list.advancedSearch')}
          aria-label={searchOpen ? t('master_document.list.hideAdvancedSearch') : t('master_document.list.advancedSearch')}
          className={clsx(
            'absolute right-1.5 inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors',
            searchOpen
              ? 'bg-primary-600 text-white'
              : 'text-content-tertiary hover:text-primary-600 hover:bg-[#eff8ff] dark:hover:bg-primary-900/20',
          )}
        >
          {searchOpen
            ? <ChevronDownIcon className="w-4 h-4 rotate-180" />
            : <FunnelIcon className="w-4 h-4" />}
        </button>
      )}
    </div>
  ) : null;

  return (
    // Contenedor SIN caja: ni fondo, ni borde, ni sombra. La única caja del
    // listado es la de la tabla, igual que en MasterCrud — cuyo root es
    // `flex flex-col w-full relative flex-1 min-h-0 h-full` y cuyo contenido va
    // en un envoltorio con el aire de la página. La cadena de `min-h-0` es la
    // que hace que la tabla ocupe el alto disponible en vez de desbordar la
    // página (ADR-027 §C.5).
    <div className={clsx('flex flex-col w-full relative flex-1 min-h-0 h-full', className)}>
      <div className="flex-1 min-h-0 flex flex-col w-full px-4 md:px-6 py-4 md:py-6 gap-4 md:gap-6">

        {/* ── Toolbar: título + compañía a la izquierda; búsqueda, Nuevo y kebab a la derecha ── */}
        <div className="flex-shrink-0">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full">
            <div className="flex flex-col items-start gap-1.5 min-w-0">
              {/* Título literal del canon (MasterCrudToolbar): 20px/600, tracking
                  ajustado y el azul de marca #1355b7. Antes era 24px/700 en
                  `content-primary`, que junto a un maestro se leía como otra app. */}
              <h1 className="text-xl font-semibold tracking-tight text-[#1355b7] dark:text-primary-600 truncate max-w-full">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-content-secondary dark:text-dark-content-secondary truncate max-w-full">
                  {subtitle}
                </p>
              )}
              {companies && companies.length > 0 ? (
                <CompanyDropdown
                  companies={companies}
                  selectedCompany={selectedCompany}
                  onCompanyChange={onCompanyChange}
                />
              ) : companyName ? (
                <span className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold',
                  'bg-primary-100 text-primary-600 border border-primary-600/30',
                  'dark:bg-primary-600/10 dark:text-primary-400 dark:border-primary-600/30',
                )}>
                  {companyName}
                </span>
              ) : null}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {cajaBusqueda}

              {/* Sin búsqueda básica el toggle del panel avanzado no tiene dónde
                  vivir, así que toma la geometría del kebab (36x36 con borde):
                  el mismo vocabulario de la barra, y el panel sigue alcanzable. */}
              {!cajaBusqueda && onToggleSearch && (
                <button
                  type="button"
                  onClick={onToggleSearch}
                  aria-expanded={!!searchOpen}
                  title={searchOpen ? t('master_document.list.hideAdvancedSearch') : t('master_document.list.advancedSearch')}
                  aria-label={searchOpen ? t('master_document.list.hideAdvancedSearch') : t('master_document.list.advancedSearch')}
                  className={clsx(
                    'inline-flex items-center justify-center w-9 h-9 border rounded-md transition-colors',
                    searchOpen
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'border-border-primary dark:border-dark-border-primary bg-background-secondary dark:bg-dark-bg-secondary hover:bg-[#ebebec] dark:hover:bg-dark-bg-primary text-content-tertiary',
                  )}
                >
                  <FunnelIcon className="w-4 h-4" />
                </button>
              )}

              {/* Botón «Nuevo»: icono + rótulo, con el `Button` del kit y las
                  mismas props que MasterCrud le pasa. Ausente —no deshabilitado—
                  cuando no aplica: un botón sin acción es un afordance que miente. */}
              {canCreate && onCreateNew && (
                <Button
                  type="default"
                  size="base"
                  className="!text-sm font-medium"
                  leftIcon={<PlusIcon className="w-4 h-4" />}
                  onClick={onCreateNew}
                >
                  {createNewLabel ?? t('master_document.list.new')}
                </Button>
              )}

              {/* Opciones adicionales en el kebab, como MasterCrud: lo que no es
                  la acción principal no compite con ella en la barra. */}
              {onExport && (
                <MoreOptionsMenu
                  options={[{
                    key: 'export',
                    label: t('master_document.list.export'),
                    icon: <ArrowUpTrayIcon className="w-4 h-4 text-content-tertiary" />,
                    onClick: onExport,
                  }]}
                />
              )}
            </div>
          </div>
        </div>

        {/* ── Pills de estado ── */}
        {pills.length > 0 && (
          <div className="flex-shrink-0 flex items-center gap-2 overflow-x-auto">
            {pills.map((pill) => (
              <button
                key={pill.key}
                type="button"
                onClick={() => onPillClick(pill.key)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap',
                  'transition-all duration-150 shrink-0',
                  pill.active && 'ring-2 ring-inset ring-black/15',
                )}
                style={{
                  backgroundColor: pill.bg ?? 'var(--color-primary-600, #0e79fd)',
                  color: pill.color ?? '#fff',
                }}
              >
                {pill.label}
                {pill.count != null && (
                  <span
                    className={clsx(
                      'inline-flex items-center justify-center px-1.5 min-w-[18px] h-[18px] rounded-full text-xs font-bold',
                      pill.active ? 'bg-white/20 dark:bg-black/20 text-inherit' : 'bg-background-secondary text-content-tertiary',
                    )}
                  >
                    {pill.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">

          {/* ── Panel de búsqueda avanzada ── Caja propia del canon
              (MasterCrudAdvancedSearch): blanca, borde, `rounded-lg shadow-sm`,
              cuerpo `p-5` y los dos botones al pie a la derecha. Ya no lleva la
              fila-cabecera «Búsqueda avanzada» con su chevron: el disparador es
              el embudo de la caja de búsqueda, igual que en MasterCrud. */}
          {onToggleSearch && searchOpen && searchFields.length > 0 && (
            <section
              aria-label={t('master_document.list.advancedSearch')}
              className="mb-4 flex-shrink-0 bg-white dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-lg shadow-sm"
            >
              <form
                onSubmit={(e) => { e.preventDefault(); onSearch(); }}
                onReset={(e) => { e.preventDefault(); onClearSearch(); }}
              >
                <div className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {searchFields.map((field) => (
                      <div key={field.key}>
                        <label
                          htmlFor={`mdl-search-${field.key}`}
                          className="block text-[11px] font-semibold text-content-tertiary dark:text-dark-content-tertiary tracking-[0.3px] mb-1"
                        >
                          {field.label}
                        </label>
                        {field.type === 'select' ? (
                          <select
                            id={`mdl-search-${field.key}`}
                            className={`${ADV_CONTROL_CLS} cursor-pointer`}
                            value={String(searchValues[field.key] ?? '')}
                            onChange={(e) => onSearchChange(field.key, e.target.value)}
                          >
                            <option value="">{field.placeholder ?? t('master_document.field.selectPlaceholder')}</option>
                            {field.options?.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`mdl-search-${field.key}`}
                            type={field.type}
                            autoComplete="off"
                            className={clsx(ADV_CONTROL_CLS, field.type === 'date' && 'cursor-pointer')}
                            style={field.type === 'date' ? { minWidth: 'min-content' } : undefined}
                            value={String(searchValues[field.key] ?? '')}
                            onChange={(e) => onSearchChange(field.key, e.target.value)}
                            placeholder={field.placeholder}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-end gap-2 px-5 pb-5">
                  <button
                    type="reset"
                    className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-sm font-medium whitespace-nowrap bg-transparent border border-[#93d1fd] text-primary-600 shadow-sm hover:bg-[#dbeefe] dark:hover:bg-primary-900/20 transition-all duration-150"
                  >
                    {t('master_document.list.clear')}
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 h-9 px-3 rounded-md text-sm font-medium whitespace-nowrap bg-primary-600 text-white shadow-inner hover:brightness-110 transition-all duration-150"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 shrink-0" />
                    {t('master_document.field.search')}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* ── Barra de selección masiva ── */}
          {bulkMode && (
            <div className={clsx(
              'flex-shrink-0 flex items-center justify-between gap-3 px-4 py-2.5 mb-4 rounded-lg',
              'bg-primary-custom-100 dark:bg-dark-bg-custom',
              'border border-primary-custom-300 dark:border-dark-border-custom',
            )}>
              <span className="text-sm font-semibold text-primary-custom-700 dark:text-dark-content-custom">
                {selectedRowIds.length > 0
                  ? t('master_document.list.selectedCount', { count: selectedRowIds.length })
                  : t('master_document.list.noneSelected')}
              </span>
              {bulkConvertOptions && bulkConvertOptions.length > 0 && (
                <CrossingClassMenu
                  options={bulkConvertOptions}
                  onSelect={(classId) => onBulkConvert?.(classId, selectedRowIds)}
                  disabled={selectedRowIds.length === 0}
                />
              )}
            </div>
          )}

          {/* ── Error ── */}
          {error && (
            <div className="flex-shrink-0 flex items-center justify-center gap-3 px-5 py-8">
              <span className="text-sm text-error-content">{error}</span>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold bg-error-bg text-error-content hover:bg-error-bg/80 transition-colors"
                >
                  {t('master_document.chrome.retry')}
                </button>
              )}
            </div>
          )}

          {/* ── Tabla ── Una sola caja alrededor del `Table`, con el radio del
              canon (`rounded-xl`). El `Table` va sin caja propia
              (`showBorder`/`showShadow` en false y el `className` que neutraliza
              fondo, sombra, radio y padding) para no pintar dos bordes
              concéntricos (ADR-027 §E.7). Es literalmente lo que hace
              MasterCrud. */}
          {!error && (
            rows.length === 0 && !loading && !hayFiltroActivo && emptyState ? (
              <div className="flex flex-1 min-h-0 flex-col items-center justify-center py-16 text-center px-5">
                {emptyState.icon && (
                  <div className="mb-4 text-border-primary dark:text-dark-border-primary">{emptyState.icon}</div>
                )}
                <p className="text-base font-semibold text-content-primary dark:text-dark-content-primary">
                  {emptyState.title}
                </p>
                {emptyState.description && (
                  <p className="text-sm text-content-secondary dark:text-dark-content-secondary mt-1 max-w-sm">
                    {emptyState.description}
                  </p>
                )}
                {emptyState.action && (
                  <Button
                    type="default"
                    size="base"
                    className="!text-sm font-medium mt-4"
                    onClick={emptyState.action.onClick}
                  >
                    {emptyState.action.label}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex flex-col flex-1 min-h-0 bg-white dark:bg-dark-bg-primary rounded-xl border border-border-primary dark:border-dark-border-primary overflow-hidden w-full">
                <Table
                  columns={tableColumns}
                  data={rows}
                  loading={loading}
                  emptyMessage={emptyState?.title ?? t('master_document.list.noResults')}
                  variant="basic"
                  showBorder={false}
                  showShadow={false}
                  showPadding={false}
                  fullWidth
                  stickyHeader
                  accentHover
                  dense
                  // La FILA no navega por defecto. Medido en MasterCrud (`/app/items`,
                  // 2026-09-09): sus filas son `tabindex: null` y `cursor: auto` — no
                  // son un enlace. Aquí lo eran por la resolución interna de
                  // `MasterDocumentPage`, así que cualquier clic dentro de la fila
                  // —incluido fallar un botón por dos píxeles— sacaba al usuario de la
                  // lista. La puerta al detalle es «Editar».
                  //
                  // Se respeta el opt-in: `MasterDocumentPage` sólo pasa este callback
                  // cuando el host declaró su propio `onRowClick` (lo hace, p. ej.,
                  // `EcdListPage`), y ahí la fila SÍ es un destino elegido a propósito.
                  onRowClick={onRowClick}
                  pagination={paginacion}
                  className="!bg-transparent !shadow-none !rounded-none !p-0"
                />
              </div>
            )
          )}
        </div>
      </div>

      {/* Confirmación de acción de fila (DEF-4) */}
      {pendingConfirm && (
        <ConfirmDialog
          message={pendingConfirm.message}
          onConfirm={pendingConfirm.run}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
    </div>
  );
}
